import React, { useState, useCallback, useEffect } from 'react'
import {
  Autocomplete,
  TextField,
  CircularProgress,
  AutocompleteProps,
} from '@mui/material'
// 軽量 debounce（cancel 付き）。外部依存(lodash)を避けるためのローカル実装
function debounce<TArgs extends unknown[]>(
  fn: (...args: TArgs) => void,
  wait: number
): ((...args: TArgs) => void) & { cancel: () => void } {
  let timer: ReturnType<typeof setTimeout> | null = null
  const debounced = (...args: TArgs) => {
    if (timer) clearTimeout(timer)
    timer = setTimeout(() => fn(...args), wait)
  }
  debounced.cancel = () => {
    if (timer) clearTimeout(timer)
    timer = null
  }
  return debounced
}

export interface AsyncAutocompleteProps<T> {
  label: string
  searchApi: (query: string) => Promise<T[]>
  getOptionLabel: (option: T | string) => string
  value: T | string | null
  onChange: (value: T | string | null) => void
  disabled?: boolean
  required?: boolean
  error?: boolean
  helperText?: string
  placeholder?: string
  minInputLength?: number
  debounceMs?: number
  noOptionsText?: string
  isOptionEqualToValue?: (option: T, value: T) => boolean
  renderOption?: AutocompleteProps<T, false, false, true>['renderOption']
  size?: 'small' | 'medium'
  fullWidth?: boolean
  sx?: any
  freeSolo?: boolean
  loadOnOpen?: boolean // ドロップダウンを開いた時点で初期データを読み込む
}

function AsyncAutocomplete<T>({
  label,
  searchApi,
  getOptionLabel,
  value,
  onChange,
  disabled = false,
  required = false,
  error = false,
  helperText,
  placeholder,
  minInputLength = 1,
  debounceMs = 300,
  noOptionsText = '該当なし',
  isOptionEqualToValue,
  renderOption,
  size = 'medium',
  fullWidth = true,
  sx,
  freeSolo = false,
  loadOnOpen = false,
}: AsyncAutocompleteProps<T>) {
  const [inputValue, setInputValue] = useState('')
  const [options, setOptions] = useState<T[]>([])
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [initialLoaded, setInitialLoaded] = useState(false)

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce(async (query: string) => {
      if (query.length < minInputLength) {
        setOptions([])
        setLoading(false)
        return
      }

      setLoading(true)
      try {
        const results = await searchApi(query)
        setOptions(results)
      } catch (error) {
        console.error('AsyncAutocomplete search error:', error)
        setOptions([])
      } finally {
        setLoading(false)
      }
    }, debounceMs),
    [searchApi, minInputLength, debounceMs]
  )

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel()
    }
  }, [debouncedSearch])

  // Load initial data when dropdown opens (if loadOnOpen is true)
  const handleOpen = useCallback(async () => {
    setOpen(true)
    if (loadOnOpen && !initialLoaded && options.length === 0) {
      setLoading(true)
      try {
        const results = await searchApi('')
        setOptions(results)
        setInitialLoaded(true)
      } catch (error) {
        console.error('AsyncAutocomplete initial load error:', error)
        setOptions([])
      } finally {
        setLoading(false)
      }
    }
  }, [loadOnOpen, initialLoaded, options.length, searchApi])

  // Handle input change
  // reason: "input" = ユーザー入力, "reset" = 値選択/クリア, "clear" = クリアボタン
  const handleInputChange = useCallback(
    (_event: React.SyntheticEvent, newInputValue: string, reason: string) => {
      setInputValue(newInputValue)
      // reason が "input" の場合のみ検索をトリガー
      if (reason === 'input') {
        if (newInputValue.length >= minInputLength) {
          setLoading(true)
          debouncedSearch(newInputValue)
        } else if (!loadOnOpen) {
          // loadOnOpen でない場合のみオプションをクリア
          setOptions([])
          setLoading(false)
        }
      } else {
        // 選択やクリア時はローディングを停止
        setLoading(false)
        debouncedSearch.cancel()
      }
    },
    [debouncedSearch, minInputLength, loadOnOpen]
  )

  // Handle selection change
  const handleChange = useCallback(
    (_event: React.SyntheticEvent, newValue: T | string | null) => {
      onChange(newValue)
    },
    [onChange]
  )

  return (
    <Autocomplete
      open={open}
      onOpen={handleOpen}
      onClose={() => setOpen(false)}
      value={value}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onChange={handleChange}
      options={options}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      loading={loading}
      disabled={disabled}
      noOptionsText={!loadOnOpen && inputValue.length < minInputLength ? `${minInputLength}文字以上入力してください` : noOptionsText}
      renderOption={renderOption}
      size={size}
      fullWidth={fullWidth}
      sx={sx}
      freeSolo={freeSolo as any}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          error={error}
          helperText={helperText}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  )
}

export default AsyncAutocomplete
